import React, { useState, useContext } from 'react';
import { Box, Typography, Button, Paper, Skeleton } from '@mui/material';
import { useQuery } from '@tanstack/react-query'; 

import FingerprintIcon from '@mui/icons-material/Fingerprint';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'; 
import { useQuickAction } from './DataQuickActionContext'; 

import AuthContext from './auth/Context'; 
import CheckInDialog from './CheckInManager';

// Style chung cho các Button hành động - Đã tối ưu xuống dòng cho Mobile
const commonButtonSx = {
  flex: 1,
  // Trên mobile hạ minWidth xuống để vừa vặn hơn, tăng chiều cao lên một chút để chứa 2 dòng
  minWidth: { xs: '75px', sm: '120px' },
  height: { xs: '56px', sm: '44px' }, 
  p: { xs: '4px 8px', sm: '8px 16px' },
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: { xs: '11px', sm: '13.5px' },
  letterSpacing: '-0.2px',
  boxShadow: 'none',
  display: 'flex',
  // Trên mobile xếp Icon trên - Text dưới, lên Desktop xếp ngang bình thường
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: 'center',
  justifyContent: 'center',
  gap: { xs: '2px', sm: '6px' },
  lineHeight: 1.2, // Giúp text 2 dòng không bị quá xa nhau
  textAlign: 'center',
  transition: 'all 0.2s ease-in-out',
  '&:hover': { 
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-1px)'
  },
  '&:active': {
    transform: 'translateY(0)'
  }
};

const formatTime = (timeString) => {
  if (!timeString) return null;
  const date = new Date(timeString);
  if (isNaN(date.getTime())) return null;
  return date.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

function TimekeepingWidget({ sx = {} }) {
  const context = useContext(AuthContext);
  const { openAdd } = useQuickAction(); 
  const [openCheckIn, setOpenCheckIn] = useState(false);

  const { data: chamcongData, refetch: refetchChamcong, error: errorChamCong, isLoading } = useQuery({
    queryKey: ['chamcongInfo'],
    queryFn: async () => {
      try {
        return await context.apis.chamcong(context.userInfo?.token, context.userInfo?.email);
      } catch(e) {
        console.error("Lỗi khi lấy dữ liệu chấm công", e);
        throw e;
      }
    },
    refetchInterval: 3600000, 
    staleTime: 3600000,
    enabled: !!context?.apis?.chamcong
  });

  const hasRecord = chamcongData && (chamcongData.gio_vao || chamcongData.gio_ra);

  return (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex', 
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'stretch', lg: 'center' },
        justifyContent: 'space-between', 
        gap: 2, 
        p: 0.5,
        ...sx 
      }}
    >
      {/* Khối hiển thị Trạng thái / Giờ giấc */}
      <Paper 
        elevation={0}
        sx={{ 
          p: '10px 16px',
          flex: 1,
          borderRadius: '12px', 
          bgcolor: 'background.paper', 
          border: '1px solid', 
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          minHeight: '44px',
          boxSizing: 'border-box',
        }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
            <Skeleton variant="text" width="40%" height={24} />
            <Skeleton variant="text" width="30%" height={24} />
          </Box>
        ) : !hasRecord && !errorChamCong ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, width: '100%' }}>
            <Box 
              sx={{ 
                width: 8, 
                height: 8, 
                borderRadius: '50%', 
                bgcolor: 'warning.main',
                animation: 'pulse 2s infinite ease-in-out',
                '@keyframes pulse': {
                  '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(237, 108, 2, 0.7)' },
                  '70%': { transform: 'scale(1)', boxShadow: '0 0 0 6px rgba(237, 108, 2, 0)' },
                  '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(237, 108, 2, 0)' }
                }
              }} 
            />
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '13.5px' }}>
              Bạn chưa ghi nhận công hôm nay. Hãy chấm công nhé!
            </Typography>
          </Box>
        ) : errorChamCong ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', width: '100%' }}>
            <WarningAmberIcon fontSize="small" />
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '13.5px' }}>
              Không thể kết nối dữ liệu chấm công.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 }, width: '100%', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '13.5px' }}>
                Hôm nay:
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
              <AccessTimeIcon sx={{ fontSize: '16px', color: 'text.disabled' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13.5px' }}>
                Vào: <Box component="span" sx={{ fontWeight: 600, color: 'success.main' }}>{formatTime(chamcongData?.gio_vao) || '--:--'}</Box>
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
              <AccessTimeIcon sx={{ fontSize: '16px', color: 'text.disabled' }} />
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '13.5px' }}>
                Ra: <Box component="span" sx={{ fontWeight: 600, color: chamcongData?.gio_ra ? 'primary.main' : 'text.disabled' }}>{formatTime(chamcongData?.gio_ra) || '--:--'}</Box>
              </Typography>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Khối các nút bấm Action */}
      <Box 
        sx={{ 
          display: 'flex', 
          gap: 1, 
          boxSizing: 'border-box',
          overflowX: 'auto',
          width: { xs: '100%', lg: 'auto' },
          '&::-webkit-scrollbar': { height: '0px' }, 
        }}
      >
        {/* Nút Chấm Công chính */}
        <Button 
          variant="contained" 
          onClick={() => setOpenCheckIn(true)} 
          sx={{ 
            ...commonButtonSx, 
            bgcolor: 'primary.main', 
            color: 'primary.contrastText',
            '&:hover': { bgcolor: 'primary.dark' }
          }}
        >
          <FingerprintIcon sx={{ fontSize: '20px' }} />
          <span>Chấm công</span>
        </Button>

        {/* Nút Nghỉ Phép */}
        <Button 
          variant="text" 
          onClick={() => openAdd("dknphep", null, "Đăng ký nghỉ phép", {}, () => context.alert("Đã đăng ký nghỉ phép thành công"))} 
          sx={{ 
            ...commonButtonSx, 
            color: 'text.primary', 
            bgcolor: 'action.hover',
            '&:hover': { bgcolor: 'action.selected', color: 'warning.main' }
          }}
        >
          <EventBusyIcon sx={{ fontSize: '20px', color: 'warning.main' }} />
          <span>Nghỉ phép</span>
        </Button>

        {/* Nút Đăng Ký Phòng Họp */}
        <Button 
          variant="text" 
          onClick={() => openAdd("dkphop", null, "Đăng ký phòng họp", {}, () => context.alert("Đã đặt phòng họp thành công"))} 
          sx={{ 
            ...commonButtonSx, 
            color: 'text.primary', 
            bgcolor: 'action.hover',
            '&:hover': { bgcolor: 'action.selected', color: 'success.main' }
          }}
        >
          <MeetingRoomIcon sx={{ fontSize: '20px', color: 'success.main' }} />
          <span>Phòng họp</span>
        </Button>

        {/* Nút Thêm Công Việc */}
        <Button 
          variant="text" 
          onClick={() => openAdd("task", null, "Thêm công việc", {}, () => context.alert("Đã thêm thành công một công việc mới"))} 
          sx={{ 
            ...commonButtonSx, 
            color: 'text.primary', 
            bgcolor: 'action.hover',
            '&:hover': { bgcolor: 'action.selected', color: 'info.main' }
          }}
        >
          <AssignmentIcon sx={{ fontSize: '20px', color: 'info.main' }} />
          <span>Công việc</span>
        </Button>
      </Box>

      {openCheckIn && (
        <CheckInDialog open={openCheckIn} onClose={() => { setOpenCheckIn(false); refetchChamcong(); }} />
      )}
    </Box>
  );
}

export default React.memo(TimekeepingWidget);